/**
 * Store Implementation
 * Wraps a platform storage adapter (LocalStorageAdapter/ChromeStorageAdapter/
 * FileStorageAdapter/IndexedDBAdapter) to conform to the Store interface used
 * by session-manager.
 *
 * KNOWN_COLLECTIONS covers the static set of collections the UI/session-manager
 * writes (metadata, llm, conv_index, compressed). Dynamic `conv:${id}` entries
 * are NOT enumerable via a static list, so `clear()` additionally reads
 * `conv_index` under the target namespace and removes every `conv:${id}` key
 * it finds — this keeps `clear()` reachable for all conversation payloads
 * regardless of id.
 */

const KNOWN_COLLECTIONS = ['metadata', 'conv_index', 'compressed']

export class Store {
  adapter: any

  constructor(storageAdapter: any) {
    this.adapter = storageAdapter
  }

  _key(namespace: string, userId: string, collection: string) {
    return `myagent_store:${namespace}:${userId}:${collection}`
  }

  async get(namespace: string, userId: string, collection: string) {
    const raw = await this.adapter.get(this._key(namespace, userId, collection))
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch (e: any) {
      throw new Error(`[Store] Corrupted data for ${collection}: ${e.message}`)
    }
  }

  async set(namespace: string, userId: string, collection: string, data: any) {
    await this.adapter.set(this._key(namespace, userId, collection), JSON.stringify(data))
  }

  async delete(namespace: string, userId: string, collection: string) {
    await this.adapter.remove(this._key(namespace, userId, collection))
  }

  async clear(namespace: string, userId: string) {
    // Dynamic sweep: enumerate conversation ids from conv_index first.
    const index = (await this.get(namespace, userId, 'conv_index')) || []
    const convKeys = Array.isArray(index)
      ? index.map((entry: any) => `conv:${entry.id}`).filter((k: string) => k !== 'conv:undefined')
      : []
    // Static sweep: fixed top-level collections (metadata/llm/conv_index/compressed).
    const allKeys = [...convKeys, ...KNOWN_COLLECTIONS]
    await Promise.all(allKeys.map(c => this.adapter.remove(this._key(namespace, userId, c))))
  }
}
